// src/mocks/index.js
import dayjs from 'dayjs'

/** 组织树（parentId 用于增删改定位；onCount/allCount 会在 recalcCounts 时自动计算） */
let orgTree = [
  {
    id: 1, parentId: null, name: '园林事业部', onCount: 0, allCount: 0, children: [
      { id: 11, parentId: 1, name: '一队', onCount: 0, allCount: 0, children: [] },
      { id: 12, parentId: 1, name: '二队', onCount: 0, allCount: 0, children: [] },
    ]
  },
  {
    id: 2, parentId: null, name: '环卫事业部', onCount: 0, allCount: 0, children: [
      { id: 21, parentId: 2, name: '东片区', onCount: 0, allCount: 0, children: [] },
      { id: 22, parentId: 2, name: '西片区', onCount: 0, allCount: 0, children: [] },
    ]
  }
]

/** 花名册（保留你之前的字段） */
const persons = Array.from({ length: 40 }).map((_, i) => ({
  id: i + 1,
  emp_no: 'RJ' + String(1000 + i),
  name: '员工' + (i + 1),
  gender: i % 2 ? '男' : '女',
  age: 28 + (i % 5),
  birth_date: '1995-01-01',
  id_card_no: '65******************' + (i % 10),
  political_status: '群众',
  marital_status: i % 3 ? '未婚' : '已婚',
  nation: '汉',
  native_place: '新疆',
  household_type: '城镇',
  reg_address: '新疆博乐市',
  home_address: '新疆博乐市XX路XX号',

  company: '润景',
  dept1: i % 2 ? '园林事业部' : '环卫事业部',
  dept2: i % 2 ? '一队' : '东片区',
  dept3: '-',
  dept4: '-',
  position: '一线人员',

  grade: String((i % 5) + 1),
  grade_series: 'A',
  job_level: 'L' + ((i % 3) + 1),
  salary: String(4500 + (i % 5) * 200),
  duty_date: '2023-01-01',

  join_date: dayjs().subtract(300 - i, 'day').format('YYYY-MM-DD'),
  leave_date: i % 7 === 0 ? dayjs().subtract(10, 'day').format('YYYY-MM-DD') : '',
  probation: '6个月',
  seniority: '2年',
  over_age: false,

  education: '大专',
  school: '新疆交通职业大学',
  major: '计算机',
  title: '-',

  email: 'demo@example.com',
  phone: '1980000' + String(1000 + i),
  emergency_contact: '张三',
  emergency_phone: '19800000001',

  contract_type: '劳动合同',
  contract_start: '2023-01-01',
  contract_end: '2026-01-01',
  contract_remind: '2025-12-15',
  contract_times: 1,
  employment_type: '正式',
  labor_company: '',
  insurance: '五险一金',
  retired: false,
  is_xjn_worker: false,

  health_check: '2024-06 正常',
  bank_name: '工行',
  bank_card: '6222 **** **** 1234',
  remark: '—',

  org_id: i % 2 ? 11 : 21,
  org_name: i % 2 ? '园林事业部/一队' : '环卫事业部/东片区',
  status: i % 7 === 0 ? 'LEAVED' : 'ON',
}))

let attachments = {}

/* ---------- orgTree helpers ---------- */
function findNode(id, list = orgTree) {
  for (const n of list) {
    if (n.id === id) return n
    if (n.children && n.children.length) {
      const r = findNode(id, n.children)
      if (r) return r
    }
  }
  return null
}
function findParent(id, list = orgTree, parent = null) {
  for (const n of list) {
    if (n.id === id) return parent
    if (n.children && n.children.length) {
      const r = findParent(id, n.children, n)
      if (r) return r
    }
  }
  return null
}
function collectIds(node, acc = []) {
  if (!node) return acc
  acc.push(node.id)
  ;(node.children || []).forEach(c => collectIds(c, acc))
  return acc
}
function recalcCounts() {
  function sum(node) {
    let on = 0, all = 0
    // count direct persons under this node
    for (const p of persons) {
      if (p.org_id === node.id) {
        all += 1
        if (p.status === 'ON') on += 1
      }
    }
    // plus children
    for (const c of (node.children || [])) {
      const s = sum(c)
      on += s.on; all += s.all
    }
    node.onCount = on; node.allCount = all
    return { on, all }
  }
  for (const root of orgTree) sum(root)
}
recalcCounts()

export const mockApi = {
  /* ------------ ORG API ------------- */
  async getOrgTree() {
    recalcCounts()
    return { data: orgTree }
  },

  async createOrg(payload) {
    // payload: { name, parentId }  parentId 可为 null 表示新增根节点
    const id = Math.max(0, ...flattenIds(orgTree)) + 1
    const node = { id, parentId: payload.parentId || null, name: payload.name, onCount: 0, allCount: 0, children: [] }
    if (payload.parentId == null) {
      orgTree.push(node)
    } else {
      const parent = findNode(Number(payload.parentId))
      if (!parent) throw new Error('parent not found')
      parent.children = parent.children || []
      parent.children.push(node)
    }
    recalcCounts()
    return { data: node }
  },

  async updateOrg(id, payload) {
    const node = findNode(Number(id))
    if (!node) throw new Error('not found')
    if (payload.name != null) node.name = payload.name
    // 这里不做换父级（防止复杂度上来），如果需要可再加 moveOrg
    recalcCounts()
    return { data: node }
  },

  async deleteOrg(id) {
    id = Number(id)
    const node = findNode(id)
    if (!node) throw new Error('not found')
    const parent = findParent(id)
    // 收集要删除的所有 id，并把这些人员的 org 置空
    const ids = collectIds(node, [])
    for (const p of persons) {
      if (ids.includes(p.org_id)) {
        p.org_id = 0
        p.org_name = ''
      }
    }
    if (parent) {
      parent.children = parent.children.filter(c => c.id !== id)
    } else {
      orgTree = orgTree.filter(r => r.id !== id)
    }
    recalcCounts()
    return { data: true }
  },

  /* ------------ PERSON API ----------- */
  async listPersons(params) {
    const { page = 1, size = 20, keyword = '', status, grade, orgId } = params || {}
    let rows = persons.slice()
    if (keyword) rows = rows.filter(r => (r.name + r.emp_no + (r.id_card_no || '') + (r.phone || '')).includes(keyword))
    if (status) rows = rows.filter(r => r.status === status)
    if (grade) rows = rows.filter(r => String(r.grade) === String(grade))
    if (orgId) rows = rows.filter(r => Number(r.org_id) === Number(orgId))
    const total = rows.length
    const start = (page - 1) * size
    const end = start + Number(size)
    return { data: { items: rows.slice(start, end), total } }
  },

  async getPerson(id) {
    const row = persons.find(p => p.id === Number(id))
    return { data: row }
  },

  async createPerson(payload) {
    const id = persons.length ? Math.max(...persons.map(p => p.id)) + 1 : 1
    const row = { id, status: 'ON', ...payload }
    persons.push(row)
    recalcCounts()
    return { data: row }
  },

  async updatePerson(id, payload) {
    const idx = persons.findIndex(p => p.id === Number(id))
    if (idx > -1) {
      persons[idx] = { ...persons[idx], ...payload }
      recalcCounts()
      return { data: persons[idx] }
    }
    throw new Error('not found')
  },

  async setLeave(id) {
    const row = persons.find(p => p.id === Number(id))
    if (row) row.status = 'LEAVED'
    recalcCounts()
    return { data: row }
  },

  async listAttachments(personId) {
    return { data: attachments[personId] || [] }
  },

  async uploadAttachment(personId, file) {
    const list = attachments[personId] || (attachments[personId] = [])
    list.push({ id: Date.now(), file_name: file.name, file_size: file.size, url: '#', uploaded_at: dayjs().format('YYYY-MM-DD HH:mm:ss') })
    return { data: list[list.length - 1] }
  },
}

/* util */
function flattenIds(list = orgTree, acc = []) {
  for (const n of list) {
    acc.push(n.id)
    if (n.children && n.children.length) flattenIds(n.children, acc)
  }
  return acc
}
